/**
 * Optional anti-bot protection.
 *
 * Entirely opt-in: when no site key is configured this module resolves to an
 * empty payload and forms behave exactly as before. Enable it by setting
 * VITE_PUBLIC_TURNSTILE_SITE_KEY or VITE_PUBLIC_RECAPTCHA_SITE_KEY and adding
 * the matching SECRET key on your server.
 *
 * The produced token field names match both our PHP and Node backends:
 *   Cloudflare Turnstile -> "cf-turnstile-response"
 *   Google reCAPTCHA v3  -> "g-recaptcha-response"
 */
import { RECAPTCHA_SITE_KEY, TURNSTILE_SITE_KEY } from "@/config/forms";

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
    turnstile?: {
      render: (
        el: HTMLElement,
        options: Record<string, unknown>,
      ) => string;
      execute: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load anti-bot script"));
    document.head.appendChild(script);
  });
}

async function getRecaptchaToken(): Promise<string> {
  await loadScript(`https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`);
  const grecaptcha = window.grecaptcha;
  if (!grecaptcha) return "";
  return new Promise<string>((resolve) => {
    grecaptcha.ready(() => {
      grecaptcha
        .execute(RECAPTCHA_SITE_KEY, { action: "submit" })
        .then((token) => resolve(token || ""))
        .catch(() => resolve(""));
    });
  });
}

async function getTurnstileToken(): Promise<string> {
  await loadScript("https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit");
  const turnstile = window.turnstile;
  if (!turnstile) return "";
  return new Promise<string>((resolve) => {
    const container = document.createElement("div");
    container.style.display = "none";
    document.body.appendChild(container);

    let widgetId = "";
    const finish = (token: string) => {
      try {
        if (widgetId) turnstile.remove(widgetId);
      } catch {
        /* noop */
      }
      container.remove();
      resolve(token);
    };

    try {
      widgetId = turnstile.render(container, {
        sitekey: TURNSTILE_SITE_KEY,
        size: "invisible",
        callback: (token: string) => finish(token || ""),
        "error-callback": () => finish(""),
      });
      turnstile.execute(widgetId);
    } catch {
      finish("");
    }
  });
}

/** Returns the extra payload fields required by the server, or {} when disabled. */
export async function getAntiBotPayload(): Promise<Record<string, string>> {
  try {
    if (RECAPTCHA_SITE_KEY) {
      const token = await getRecaptchaToken();
      return token ? { "g-recaptcha-response": token } : {};
    }
    if (TURNSTILE_SITE_KEY) {
      const token = await getTurnstileToken();
      return token ? { "cf-turnstile-response": token } : {};
    }
  } catch {
    /* Never block a real submission because anti-bot failed to load. */
  }
  return {};
}